import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { IViaCepAnswer } from '../models/viacep-answer.model';

@Injectable({
  providedIn: 'root'
})
export class PostalCodeService {
  private baseUrl = 'https://viacep.com.br/ws';

  constructor(private http: HttpClient) {}

  /**
   * Verifica se o país indicado é Brasil
   */
  isBrazil(country: string): boolean {
    if (!country) return false;
    
    return country.toLowerCase() === 'brasil' || 
           country.toLowerCase() === 'brazil' || 
           country === 'BR';
  }
  
  /**
   * Validador universal de código postal que aceita qualquer formato
   * @param isRequired Define se o campo é obrigatório
   */
  postalCodeValidator(isRequired: boolean = false) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      // Se o campo está vazio e é obrigatório, retorna erro
      if (isRequired && (!value || value.toString().trim() === '')) {
        return { required: true };
      }
      
      // Aceita QUALQUER formato de código postal - sem validação adicional
      // Não valida número de dígitos, formato ou qualquer outro requisito
      return null;
    };
  }
  
  /**
   * Formata um CEP brasileiro (apenas para visualização)
   */
  formatBrazilianCEP(value: string): string {
    if (!value) return '';
    
    // Remove caracteres não numéricos
    const digits = value.replace(/\D/g, '');
    
    // Se tiver 8 dígitos, formata como CEP brasileiro padrão
    if (digits.length === 8) {
      return `${digits.substring(0, 5)}-${digits.substring(5, 8)}`;
    }
    
    // Para outro número de dígitos, não tenta formatar
    return value;
  }
  
  /**
   * Busca endereço apenas para CEP brasileiro
   */
  async fetchAddressForBrazilianCEP(cep: string): Promise<IViaCepAnswer | null> {
    try {
      // Remove caracteres não numéricos para busca
      const digits = cep.replace(/\D/g, '');
      
      if (digits.length !== 8) {
        console.warn('CEP com formato inválido:', cep);
        return null;
      }
      
      // Tentativas com diferentes formatos/URLs para contornar problemas de CORS
      let response: IViaCepAnswer | null = null;
      let error: any = null;
      
      try {
        // Primeira tentativa: usar a API ViaCEP padrão
        response = await lastValueFrom(
          this.http.get<IViaCepAnswer>(`${this.baseUrl}/${digits}/json`)
        );
      } catch (e) {
        error = e;
        console.warn('Falha na primeira tentativa de buscar CEP:', e);
      }
      
      // Se a primeira tentativa falhar, tenta com uma configuração alternativa
      if (!response && error) {
        try {
          response = await lastValueFrom(
            this.http.get<IViaCepAnswer>(`${this.baseUrl}/${digits}/json`, {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            })
          );
        } catch (e) {
          console.error('Todas as tentativas de buscar CEP falharam:', e);
          return null;
        }
      }
      
      // Verifica se a resposta contém erro
      if (!response || response.erro) {
        console.warn('CEP não encontrado ou resposta inválida:', cep);
        return null;
      }
      
      return response;
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      return null;
    }
  }
} 